import React, { useState, useRef } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import { useLunr } from 'react-lunr'
import { useDebounce } from 'use-debounce'
import { notEmpty } from 'helpers'
import dlv from 'dlv'

import { Box, Text, Input, SVG, Link } from 'system'
import { Heading } from './Heading'
import { Subheading } from './Subheading'
import { ReactComponent as SearchIconSVG } from '../assets/icon-search.svg'

export const Search = props => {
  const localSearchData = useStaticQuery(graphql`
    query {
      localSearchReference {
        index
        store
      }
    }
  `)

  const index = dlv(localSearchData, 'localSearchReference.index')
  const store = dlv(localSearchData, 'localSearchReference.store')

  const [query, setQuery] = useState('')
  const [debouncedQuery] = useDebounce(query, 250)
  const results = useLunr(debouncedQuery, index, store)

  return (
    <Box width={1} position="relative" {...props}>
      <SVG
        x={14}
        y={15}
        svg={SearchIconSVG}
        color="gray.60"
        width="0.75rem"
        fill="transparent"
        position="absolute"
        top="50%"
        left="0.75rem"
        css={`
          transform: translateY(-50%);
        `}
      />
      <Input
        type="search"
        aria-label="Search Documentation"
        name="query"
        autoComplete="off"
        placeholder="Search everything&hellip;"
        onChange={event => setQuery(event.target.value)}
        value={query}
        bg="white"
        borderRadius="4px"
        border="1px solid"
        borderColor="purple.90"
        fontFamily="sans"
        fontSize="tiny"
        lineHeight="copy"
        outline={0}
        pr={2}
        pl={4}
        py={1}
        width={1}
        css={`
          // transition: background ${p => p.theme.transition};

          &::placeholder {
            color: ${p => p.theme.colors.gray[50]};
          }

          &:focus + .results {
            opacity: ${debouncedQuery.length ? 1 : 0};
            visibility: visible;
          }
        `}
      />
      <Box
        as="ul"
        className="results"
        bg="white"
        borderRadius="4px"
        left={0}
        opacity={0}
        position="absolute"
        py={1}
        right={0}
        top="calc(100% + 0.5rem)"
        css={`
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15),
            0 2px 4px rgba(0, 0, 0, 0.075);
          transition: opacity ${p => p.theme.transition};

          &:focus-within {
            opacity: 1;
            visibility: visible;
          }
        `}
        tabIndex={-1}
      >
        {notEmpty(debouncedQuery) &&
          (results.length < 1 ? (
            <Text
              fontSize="small"
              color="gray.30"
              textAlign="center"
              py={2}
              px={3}
            >
              No results found for &ldquo;
              <Text
                as="strong"
                color="black"
                display="inline"
                fontWeight="semibold"
              >
                {debouncedQuery}
              </Text>
              &rdquo;
            </Text>
          ) : (
            results.map(result => (
              <Box key={result.id} as="li">
                <Link
                  href={dlv(result, 'slug')}
                  display="block"
                  py={2}
                  px={3}
                  css={`
                    transition: ${p => p.theme.transition};
                    outline: none;

                    &:hover,
                    &:focus {
                      background-color: ${p => p.theme.colors.purple[100]};
                    }
                  `}
                >
                  {dlv(result, 'parent') && (
                    <Subheading as="p" color="purple.50" mb={1}>
                      {dlv(result, 'parent')}
                    </Subheading>
                  )}
                  <Heading as="p" color="black" fontSize="normal" mb={1}>
                    {dlv(result, 'title')}
                  </Heading>
                  {dlv(result, 'summary') && (
                    <Text
                      as="p"
                      fontSize="tiny"
                      color="gray.30"
                      lineHeight="copy"
                    >
                      {dlv(result, 'summary')}
                    </Text>
                  )}
                </Link>
              </Box>
            ))
          ))}
      </Box>
    </Box>
  )
}
